// Unified incentives model (spec: #742 Phase 2). Pure: all IO injected.
// Counting ported 1:1 from useDiscountFreeItem.getFreeProductsByCondition /
// useDiscountTiered.calculateTieredProgress; labels ported from
// components/layout/cartPopup/progressBarLabels.js (which keeps serving the
// old drawer until cutover).
import { resolveTiers } from './tierMirror';

const resolveLabelTemplate = (label, isAchieved, replacements) => {
  if (!label) return null;
  const [achieved, progress] = label.split('|');
  let text = isAchieved ? achieved : progress || achieved;
  Object.entries(replacements).forEach(([key, value]) => {
    text = text.replace(`{${key}}`, value);
  });
  return text;
};

// Ported from useDiscountFreeItem.getFreeProductsByCondition.
// NOTE: old code uses separate if-statements for allItems/products/collections
// (not else-if), so a line matching multiple matchers could theoretically be
// double-counted. In practice the API sends mutually exclusive conditions so
// behavior is identical. The spec template uses OR logic (matches once); we
// use OR logic here for correctness.
// NOTE: totPrice uses plain quantity (not effectiveQuantity) — matching old hook.
const countQualifyingLines = (condition, cartLines, giftAttrKey) =>
  (cartLines || [])
    .filter(({ node }) => !node?.attributes?.some(({ key }) => key === giftAttrKey))
    .reduce(
      (acc, { node }) => {
        if (!condition) return acc;
        const { quantity } = node;
        const componentCount = parseInt(node?.merchandise?.product?.componentCount?.value, 10);
        const effectiveQuantity = componentCount > 0 ? quantity * componentCount : quantity;
        const productId = node?.merchandise?.product?.id;
        const lineItemCollections = node?.merchandise?.product?.collections?.edges?.map(
          ({ node: c }) => c?.id
        );
        const { products, collections, allItems } = condition;
        const matches =
          allItems ||
          products?.includes(productId) ||
          collections?.some(c => lineItemCollections?.includes(c));
        if (matches) {
          acc.amount += effectiveQuantity;
          acc.strictAmount += quantity;
          acc.totPrice += quantity * node?.merchandise?.priceV2?.amount;
        }
        return acc;
      },
      { amount: 0, strictAmount: 0, totPrice: 0 }
    );

// Ported from useDiscountFreeItem.nrOfCondicionItemsInCart.
const countGiftLinesFor = (condition, cartLines, giftAttrKey) =>
  (cartLines || []).reduce((acc, { node }) => {
    const isGift = node?.attributes?.some(({ key }) => key === giftAttrKey);
    if (!isGift) return acc;
    if (condition.freeProducts?.some(({ variantId }) => variantId === node?.merchandise?.id)) {
      return acc + node?.quantity;
    }
    return acc;
  }, 0);

const freeItemConditionToIncentive = (condition, cartLines) => {
  const giftAttrKey = '_freeItem';
  const neededQuantity = condition?.value?.quantity;
  const neededAmount = condition?.value?.amount ? parseFloat(condition.value.amount) : null;
  // Parity: old code's `if (neededQuantity)` / `if (neededAmount)` guards mean
  // a condition with no (or zero) threshold produces nothing
  if (!neededQuantity && !neededAmount) return null;
  const counted = countQualifyingLines(condition, cartLines, giftAttrKey);

  const kind = neededQuantity ? 'quantity' : 'amount';
  const target = neededQuantity || neededAmount;
  // freeItem amount thresholds count the condition-scoped undiscounted line
  // total (legacy freeItemProgress.currentAmount = lineItemsPassed.totPrice)
  const current = kind === 'quantity' ? counted.amount : counted.totPrice;
  const satisfied = current >= target;

  const timesSatisfied = satisfied ? Math.floor(current / target) : 0;
  const timesSatisfiedStrict = satisfied ? Math.floor(counted.strictAmount / target) : 0;
  let itemsLeft = 0;
  if (satisfied) {
    const inCart = countGiftLinesFor(condition, cartLines, giftAttrKey);
    const limit = condition?.usesPerOrderLimit;
    itemsLeft = inCart < timesSatisfied && (!limit || limit > inCart) ? timesSatisfied - inCart : 0;
  }

  // Availability (#1021): unavailable gifts (OOS tag / non-ACTIVE product) are
  // dropped from the reward so nothing downstream offers them, but the full
  // list keeps feeding countGiftLinesFor above so a carted OOS gift still
  // counts until the cleanup pass removes it.
  const availableProducts = condition.freeProducts
    ? condition.freeProducts.filter(p => p.available !== false)
    : null;

  return {
    type: 'freeItem',
    discountId: condition.discountId,
    title: condition.discountTitle,
    threshold: {
      kind,
      current,
      target,
      remaining: Math.max(0, (target || 0) - current),
      satisfied,
    },
    reward: { products: availableProducts, discountPercentage: null, tiers: null },
    giftUnavailable: Boolean(condition.freeProducts?.length && !availableProducts.length),
    usesPerOrderLimit: condition?.usesPerOrderLimit,
    itemsLeft,
    timesSatisfied,
    timesSatisfiedStrict,
    collectionTitles: condition.collectionTitles,
  };
};

// Ported from useDiscountTiered.calculateTieredProgress, but now multiplies by
// componentCount so a bundle counts toward tiers the same way the Bundle
// Friendly Function does at checkout (component_count * line.quantity) — closes
// the old display-vs-checkout bundle gap (#742, 2026-06-17).
const buildTieredIncentive = (
  shopifyTiers,
  tierMirrorEntries,
  tierMirrorCollectionId,
  cartLines
) => {
  const tiers = resolveTiers(shopifyTiers, tierMirrorEntries, tierMirrorCollectionId);
  const sorted = (tiers || [])
    .filter(t => t.minimumQuantity)
    .sort((a, b) => a.minimumQuantity - b.minimumQuantity);
  if (!sorted.length || !cartLines?.length) return null;

  const scope = sorted[0];
  const eligibleQuantity = cartLines.reduce((acc, { node }) => {
    const { quantity } = node;
    // Bundles count as their component_count toward tiers — matches the Bundle
    // Friendly Function at checkout (component_count * line.quantity) and the
    // free-item countQualifyingLines counting (#742, 2026-06-17).
    const componentCount = parseInt(node?.merchandise?.product?.componentCount?.value, 10);
    const effectiveQuantity = componentCount > 0 ? quantity * componentCount : quantity;
    const productId = node?.merchandise?.product?.id;
    const lineItemCollections = node?.merchandise?.product?.collections?.edges?.map(
      ({ node: c }) => c?.id
    );
    if (scope.allItems) return acc + effectiveQuantity;
    if (scope.eligibleProducts?.includes(productId)) return acc + effectiveQuantity;
    if (scope.eligibleCollections?.some(c => lineItemCollections?.includes(c.id)))
      return acc + effectiveQuantity;
    return acc;
  }, 0);

  const currentTier =
    sorted
      .slice()
      .reverse()
      .find(t => eligibleQuantity >= t.minimumQuantity) || null;
  const nextTier = sorted.find(t => eligibleQuantity < t.minimumQuantity) || null;
  const targetTier = nextTier || sorted[sorted.length - 1];

  return {
    type: 'tiered',
    discountId: sorted[0].discountId || 'tier-mirror',
    title: sorted[0].discountTitle || null,
    threshold: {
      kind: 'quantity',
      current: eligibleQuantity,
      target: targetTier.minimumQuantity,
      remaining: Math.max(0, targetTier.minimumQuantity - eligibleQuantity),
      // Parity with getTieredDiscountLabel: achieved as soon as ANY tier is
      // reached (isAchieved = !!currentTier), not only the highest target
      satisfied: Boolean(currentTier),
    },
    reward: {
      products: null,
      discountPercentage: (currentTier || targetTier).discountPercentage ?? null,
      tiers: sorted,
    },
    nextTierPercentage: targetTier.discountPercentage ?? null,
    collectionTitle: targetTier.eligibleCollections?.[0]?.title || null,
  };
};

// --- label resolution (ported from progressBarLabels.js, formatPrice injected) ---
const freeItemQuantityLabel = (incentive, translations) => {
  const qty = incentive.threshold.remaining;
  const label = translations?.freeItem?.progressbarLabel;
  // Hardcoded fallbacks ported from progressBarLabels.js:43-46
  if (!label) {
    return incentive.threshold.satisfied
      ? 'You have received your gift!'
      : `${qty} more product${qty > 1 ? 's' : ''} left to receive your gift`;
  }
  return resolveLabelTemplate(label, incentive.threshold.satisfied, {
    remainingQuantity: qty,
    products: qty > 1 ? 'products' : 'product',
  });
};

const amountMilestonesLabel = ({
  giftIncentives,
  shippingIncentive,
  translations,
  formatPrice,
  locale,
}) => {
  const milestones = giftIncentives.map(i => ({
    kind: 'gift',
    value: i.threshold.target,
    satisfied: i.threshold.satisfied,
    remaining: i.threshold.remaining,
  }));
  if (shippingIncentive) {
    milestones.push({
      kind: 'shipping',
      value: shippingIncentive.threshold.target,
      satisfied: shippingIncentive.threshold.satisfied,
      remaining: shippingIncentive.threshold.remaining,
    });
  }
  const t = translations;
  if (milestones.every(m => m.satisfied)) {
    const label =
      t?.freeItem?.progressbarAllAchievedLabel ||
      t?.freeItem?.progressbarAmountLabel ||
      t?.freeItem?.progressbarLabel;
    // Fallback ported from progressBarLabels.js:85
    if (!label) return 'You have received your gift!';
    return resolveLabelTemplate(label, true, {
      remainingAmount: '',
      remainingQuantity: '',
      products: '',
    });
  }
  const next = milestones.filter(m => !m.satisfied).sort((a, b) => a.value - b.value)[0];
  const remainingFormatted = formatPrice({ locale, price: Number(next.remaining).toFixed(2) });
  const giftAchieved = milestones.some(m => m.kind === 'gift' && m.satisfied);
  const shippingAchieved = milestones.some(m => m.kind === 'shipping' && m.satisfied);

  // Gift achieved, working toward shipping
  if (next.kind === 'shipping' && giftAchieved) {
    const label =
      t?.freeItem?.progressbarGiftAchievedLabel ||
      t?.freeItem?.progressbarShippingLabel ||
      t?.shipping?.freeShipingDifferenceMessage;
    if (!label) return `${remainingFormatted} left for free shipping`;
    return resolveLabelTemplate(label, false, {
      remainingAmount: remainingFormatted,
      difference: remainingFormatted,
    });
  }
  // Shipping achieved, working toward gift
  if (next.kind === 'gift' && shippingAchieved) {
    const label =
      t?.freeItem?.progressbarShippingAchievedLabel ||
      t?.freeItem?.progressbarAmountLabel ||
      t?.freeItem?.progressbarLabel;
    if (!label) return `${remainingFormatted} left to receive your gift`;
    return resolveLabelTemplate(label, false, {
      remainingAmount: remainingFormatted,
      remainingQuantity: remainingFormatted,
      products: '',
    });
  }
  // Neither achieved, next is shipping
  if (next.kind === 'shipping') {
    const label =
      t?.freeItem?.progressbarShippingLabel || t?.shipping?.freeShipingDifferenceMessage;
    if (!label) return `${remainingFormatted} left for free shipping`;
    return resolveLabelTemplate(label, false, {
      remainingAmount: remainingFormatted,
      difference: remainingFormatted,
    });
  }
  // Neither achieved, next is gift
  const label = t?.freeItem?.progressbarAmountLabel || t?.freeItem?.progressbarLabel;
  if (!label) return `${remainingFormatted} left to receive your gift`;
  return resolveLabelTemplate(label, false, {
    remainingAmount: remainingFormatted,
    remainingQuantity: remainingFormatted,
    products: '',
  });
};

const tieredLabel = (incentive, translations) => {
  const label = translations?.tieredDiscount?.progressbarLabel;
  const { satisfied, remaining } = incentive.threshold;
  const betweenTiers = label?.split('|')[2];
  if (satisfied && remaining > 0 && betweenTiers) {
    return resolveLabelTemplate(betweenTiers, true, {
      remainingQuantity: remaining,
      products: remaining > 1 ? 'products' : 'product',
      discountPercentage: Math.round(incentive.nextTierPercentage * 100),
    });
  }
  const pct = Math.round(
    (satisfied ? incentive.reward.discountPercentage : incentive.nextTierPercentage) * 100
  );
  const qty = incentive.threshold.remaining;
  // Hardcoded fallbacks ported from progressBarLabels.js:170-173
  if (!label) {
    return satisfied
      ? `Enjoy your ${pct}% discount`
      : `Add ${qty} more product${qty > 1 ? 's' : ''} and get ${pct}% off`;
  }
  return resolveLabelTemplate(label, satisfied, {
    remainingQuantity: qty,
    products: qty > 1 ? 'products' : 'product',
    discountPercentage: pct,
  });
};

const shippingLabel = (incentive, translations, formatPrice, locale) => {
  // Parity with cartPopup.jsx shippingMessage: STRICT > (at exactly the
  // threshold the difference message shows "€0.00 left"), and no hardcoded
  // fallback — legacy returned undefined when the translation is missing
  if (incentive.threshold.current > incentive.threshold.target) {
    return translations?.shipping?.freeShippingMessage || null;
  }
  const difference = formatPrice({
    locale,
    price: Number(incentive.threshold.target - incentive.threshold.current).toFixed(2),
  });
  return (
    translations?.shipping?.freeShipingDifferenceMessage?.replace('{difference}', difference) ||
    null
  );
};

function normalizeIncentives({
  freeItemConditions,
  shopifyTiers,
  tierMirrorEntries,
  tierMirrorCollectionId,
  cartLines,
  subTotalPrice,
  freeShippingPrice,
  hasShippingBar,
  translations,
  locale,
  formatPrice,
}) {
  const incentives = [];

  (freeItemConditions || []).forEach(c => {
    const incentive = freeItemConditionToIncentive(c, cartLines);
    if (incentive) incentives.push(incentive);
  });

  const tiered = buildTieredIncentive(
    shopifyTiers,
    tierMirrorEntries,
    tierMirrorCollectionId,
    cartLines
  );
  if (tiered) incentives.push(tiered);

  let shippingIncentive = null;
  if (hasShippingBar) {
    const current = parseFloat(subTotalPrice) || 0;
    shippingIncentive = {
      type: 'shipping',
      discountId: null,
      title: null,
      threshold: {
        kind: 'amount',
        current,
        target: freeShippingPrice,
        // >= here: the combined-bar milestones used >= in progressBarLabels.js
        // and the ProgressBar threshold icons use >=; the plain shipping
        // MESSAGE uses strict > (see shippingLabel) — both ported as-is
        remaining: Math.max(0, freeShippingPrice - current),
        satisfied: current >= freeShippingPrice,
      },
      reward: { products: null, discountPercentage: null, tiers: null },
    };
    incentives.push(shippingIncentive);
  }

  // --- progressModel: cartPopup.jsx:272-324 precedence ---
  // Branch choice by the FIRST freeItem entry's kind, like the old
  // `isAmountBased = freeItemProgress?.[0]?.type === 'amount'`
  const freeItems = incentives.filter(i => i.type === 'freeItem' && !i.giftUnavailable);
  const isAmountBased = freeItems[0]?.threshold.kind === 'amount';
  const amountBased = freeItems.filter(i => i.threshold.kind === 'amount');
  let progressModel = { label: null, bar: null };

  if (freeItems.length && isAmountBased) {
    // Combined bar only when a real free-shipping threshold exists
    // (legacy showCombinedBar = isAmountBased && hasShippingBar && price > 0)
    const showCombined = Boolean(shippingIncentive) && freeShippingPrice > 0;
    progressModel = {
      label: amountMilestonesLabel({
        giftIncentives: amountBased,
        shippingIncentive: showCombined ? shippingIncentive : null,
        translations,
        formatPrice,
        locale,
      }),
      bar: {
        current: amountBased[0].threshold.current,
        max: Math.max(
          ...amountBased.map(i => i.threshold.target),
          showCombined ? freeShippingPrice : 0
        ),
        giftThresholds: amountBased.map(i => ({
          discountId: i.discountId,
          value: i.threshold.target,
        })),
        shippingThreshold: showCombined ? freeShippingPrice : undefined,
      },
    };
  } else if (freeItems.length) {
    const first = freeItems[0];
    progressModel = {
      label: freeItemQuantityLabel(first, translations),
      bar: {
        current: first.threshold.current,
        max: first.threshold.target,
        showGiftIcon: true,
        sections: first.threshold.target,
      },
    };
  } else if (tiered) {
    const lastTierQty = tiered.reward.tiers[tiered.reward.tiers.length - 1].minimumQuantity;
    progressModel = {
      label: tieredLabel(tiered, translations),
      bar: {
        current: Math.min(tiered.threshold.current, lastTierQty),
        max: lastTierQty,
        sections: lastTierQty,
        tiers: tiered.reward.tiers,
      },
    };
  } else if (shippingIncentive) {
    progressModel = {
      label: shippingLabel(shippingIncentive, translations, formatPrice, locale),
      bar: {
        current: shippingIncentive.threshold.current,
        max: shippingIncentive.threshold.target,
      },
    };
  }

  return { incentives, progressModel };
}

export default normalizeIncentives;
